import React, { createContext, useState, useMemo, useEffect, useCallback } from 'react';

export type SupportedLocale = 'fr' | 'en' | 'es';

interface LocaleContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

export const LocaleContext = createContext<LocaleContextType>({} as LocaleContextType);

const SUPPORTED_LOCALES: SupportedLocale[] = ['fr', 'en', 'es'];

function isSupportedLocale(value: string | null): value is SupportedLocale {
  return value !== null && SUPPORTED_LOCALES.includes(value as SupportedLocale);
}

/**
 * Gets the initial locale from URL params, localStorage, or defaults to French.
 * Priority: URL ?lang= > localStorage > browser language > 'fr'
 */
function getInitialLocale(): SupportedLocale {
  if (typeof window === 'undefined') return 'fr';

  // 1. URL parameter takes highest priority
  const urlLocale = new URLSearchParams(window.location.search).get('lang');
  if (isSupportedLocale(urlLocale)) return urlLocale;

  // 2. Previously saved preference
  const stored = localStorage.getItem('locale');
  if (isSupportedLocale(stored)) return stored;

  // 3. Default to French (platform language)
  // Note: navigator.language is intentionally NOT used — SmartBeez is
  // a French-first platform, and browser language detection causes
  // surprise English for new users with English browsers.
  return 'fr';
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(getInitialLocale);

  // Persist to localStorage whenever locale changes
  useEffect(() => {
    localStorage.setItem('locale', locale);
    // Update URL param without reload
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', locale);
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
    // Set html lang attribute for accessibility
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((newLocale: SupportedLocale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
    }
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
});
